<template>
  <header class="tw-w-full tw-border-b tw-bg-background">
    <!-- Desktop: single row (logo / progress center / deadline+lang right) -->
    <div class="tw-hidden md:tw-flex tw-w-full tw-px-6 tw-h-12 tw-flex-row tw-items-center tw-gap-4">
      <img src="/assets/vitable-logo.png" alt="Vitable logo" class="tw-h-7 tw-flex-shrink-0" />
      <div class="tw-flex-1 tw-flex tw-justify-center tw-min-w-0">
        <EnrollmentFlowSteps v-if="!isPostEnrollmentRoute" />
      </div>
      <div class="tw-flex tw-items-center tw-gap-4 tw-flex-shrink-0">
        <Transition name="fade">
          <span v-if="enrollmentDeadline" class="tw-text-xs tw-font-medium tw-whitespace-nowrap">
            <span class="tw-text-secondary">{{ $t("Deadline:") }}</span>
            <b class="tw-text-foreground tw-ml-1">{{ enrollmentDeadline }}</b>
          </span>
        </Transition>
        <LanguageSelector />
      </div>
    </div>

    <!-- Mobile: stacked rows (logo + deadline + lang on top, progress below) -->
    <div class="md:tw-hidden tw-w-full tw-px-4 tw-py-2 tw-flex tw-flex-col tw-gap-2">
      <div class="tw-flex tw-items-center tw-gap-2">
        <img src="/assets/vitable-logo.png" alt="Vitable logo" class="tw-h-6 tw-flex-shrink-0" />
        <Transition name="fade">
          <span v-if="enrollmentDeadline" class="tw-ml-auto tw-text-xs tw-font-medium tw-whitespace-nowrap">
            <span class="tw-text-secondary">{{ $t("Deadline:") }}</span>
            <b class="tw-text-foreground tw-ml-1">{{ enrollmentDeadline }}</b>
          </span>
        </Transition>
        <LanguageSelector />
      </div>
      <div v-if="!isPostEnrollmentRoute" class="tw-flex tw-justify-center">
        <EnrollmentFlowSteps />
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useEnrollmentStore } from "@/modules/enrollment-new/stores/enrollment-new-store"
import { Settings } from "luxon"
import { asDateTime } from "@common/utilities/time-utils"
import LanguageSelector from "@/modules/i18n/components/LanguageSelector.vue"
import EnrollmentFlowSteps from "@/modules/enrollment-new/pages/enrollment-flow/layout/components/EnrollmentFlowSteps.vue"

const router = useRouter()
const route = useRoute()
const enrollmentStore = useEnrollmentStore()

// Post-enrollment routes (schedule-visit, download-app, and embed equivalents)
// hide the benefit-flow stepper since the user is past the elections phase.
const POST_ENROLLMENT_ROUTE_NAMES = new Set([
  "enrollment-schedule-visit",
  "enrollment-download-app",
  "embed-enrollment-schedule-visit",
  "embed-enrollment-download-app"
])

const isPostEnrollmentRoute = computed(() => {
  const name = route.name
  return typeof name === "string" && POST_ENROLLMENT_ROUTE_NAMES.has(name)
})

const enrollmentDeadline = computed(() => {
  if (enrollmentStore.activeFlowCompanyId) {
    return enrollmentStore.activeFlowCompletionDeadline
  }

  if (router.currentRoute.value.query["due"]) {
    // @ts-ignore
    const targetDate = asDateTime(parseInt(router.currentRoute.value.query["due"]))

    return targetDate.setLocale(Settings.defaultLocale).toLocaleString({ month: "short", day: "2-digit" })
  }

  if (router.currentRoute.value.query["duedate"]) {
    // @ts-ignore
    const targetDate = asDateTime(router.currentRoute.value.query["duedate"])

    return targetDate.setLocale(Settings.defaultLocale).toLocaleString({ month: "short", day: "2-digit" })
  }

  return null
})
</script>
